<script setup lang="ts">
import type { ComponentSize } from '@/types/ui';
import { Icon } from '@iconify/vue';
/**
 * UiSearchInput - Design System Search Input Component
 *
 * A specialized text input for search functionality with search icon and clear button.
 * Matches the styling of the live platform search inputs.
 *
 * @example
 * <UiSearchInput v-model="searchQuery" placeholder="Search projects..." />
 */
import { computed, ref, useId } from 'vue';

export interface UiSearchInputProps {
   /** Placeholder text */
   placeholder?: string;
   /** Accessible label for screen readers and a11y scanners. Falls back to placeholder when omitted so the input always has an accessible name. */
   ariaLabel?: string;
   /** Size variant */
   size?: Extract<ComponentSize, 'sm' | 'md' | 'lg'>;
   /** Whether the input is disabled */
   disabled?: boolean;
   /** Whether to show the clear button when there's a value */
   clearable?: boolean;
   /** Whether the input is in a loading state (shows spinner) */
   loading?: boolean;
   /** Debounce delay in milliseconds for input events */
   debounce?: number;
}

const props = withDefaults(defineProps<UiSearchInputProps>(), {
   placeholder: 'Search...',
   size: 'md',
   disabled: false,
   clearable: true,
   loading: false,
   debounce: 0,
});

const emit = defineEmits<{
   search: [value: string];
   clear: [];
   focus: [event: FocusEvent];
   blur: [event: FocusEvent];
}>();
const modelValue = defineModel<string>({ default: '' });
const id = useId();
const inputRef = ref<HTMLInputElement | null>(null);
let debounceTimer: ReturnType<typeof setTimeout> | null = null;

const hasValue = computed(() => modelValue.value && modelValue.value.length > 0);

const wrapperClasses = computed(() => {
   const sizeClasses = {
      sm: 'h-10',
      md: 'h-12',
      lg: 'h-14',
   };

   const paddingClasses = {
      sm: 'px-3',
      md: 'px-4',
      lg: 'px-6',
   };

   return [
      'flex items-center gap-2 w-full rounded-full transition-all duration-100',
      'border-0 ring-1 ring-primary focus-within:ring-2 focus-within:ring-primary',
      sizeClasses[props.size],
      paddingClasses[props.size],
      props.disabled ? 'opacity-50 cursor-not-allowed bg-neutral-light' : 'bg-white',
   ].join(' ');
});

const inputClasses = computed(() => {
   const sizeClasses = {
      sm: 'text-sm',
      md: 'text-base',
      lg: 'text-lg',
   };

   return [
      'flex-1 bg-transparent outline-none placeholder:text-neutral-dark',
      sizeClasses[props.size],
      props.disabled ? 'cursor-not-allowed' : '',
   ].join(' ');
});

const iconSize = computed(() => {
   const sizes = { sm: 'size-4', md: 'size-5', lg: 'size-6' };
   return sizes[props.size];
});

function handleInput(event: Event) {
   const value = (event.target as HTMLInputElement).value;

   if (props.debounce > 0) {
      if (debounceTimer)
         clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
         emit('search', value);
      }, props.debounce);
   }
   else {
      emit('search', value);
   }
}

function handleClear() {
   modelValue.value = '';
   emit('clear');
   emit('search', '');
   inputRef.value?.focus();
}

function handleKeydown(event: KeyboardEvent) {
   if (event.key === 'Escape' && hasValue.value) {
      event.preventDefault();
      handleClear();
   }
}

defineExpose({
   focus: () => inputRef.value?.focus(),
   clear: handleClear,
});
</script>

<template>
   <div :class="wrapperClasses">
      <!-- Search icon -->
      <Icon
         v-if="!loading"
         icon="mdi:magnify"
         class="text-neutral-dark flex-shrink-0" :class="[iconSize]"
      />

      <!-- Loading spinner -->
      <Icon
         v-else
         icon="svg-spinners:ring-resize"
         class="text-primary flex-shrink-0" :class="[iconSize]"
      />

      <!-- Input -->
      <input
         :id="id"
         ref="inputRef"
         v-model="modelValue"
         type="search"
         :placeholder="placeholder"
         :aria-label="ariaLabel ?? placeholder"
         :disabled="disabled"
         :class="inputClasses"
         autocomplete="off"
         @input="handleInput"
         @keydown="handleKeydown"
         @focus="emit('focus', $event)"
         @blur="emit('blur', $event)"
      />

      <!-- Clear button -->
      <button
         v-if="clearable && hasValue && !disabled"
         type="button"
         class="p-1 rounded-full hover:bg-neutral transition-colors text-neutral-dark hover:text-black"
         aria-label="Clear search"
         @click="handleClear"
      >
         <Icon icon="mdi:close" :class="iconSize" />
      </button>

      <!-- Append slot (e.g. inline submit button) -->
      <slot name="append" />
   </div>
</template>

<style scoped>
/*
 * Hide WebKit/Chromium's native clear glyph on <input type="search">.
 * The component renders its own accessible Clear button; the native one
 * lacks aria-label and produces a visible duplicate. Pure cosmetic
 * suppression; behaviour is unchanged.
 */
input[type="search"]::-webkit-search-cancel-button,
input[type="search"]::-webkit-search-decoration {
   -webkit-appearance: none;
   appearance: none;
   display: none;
}
</style>
